/*
 * industries
 * ----------
 * Single source of truth for the "Industries We Empower" catalog.
 *
 * Consumed by:
 *   - `SidebarDrawer.tsx`  — populates the "Industries We Empower"
 *                             mega-panel's sub-item list + per-item
 *                             preview media in col 3.
 *   - `Industries.tsx`     — drives the /industries listing page
 *                             (hero + 3-column card grid).
 *   - `IndustryDetail.tsx` — drives the full-page video hero,
 *                             overlay-card copy, and
 *                             "What sets us apart" section.
 *
 * The `id` doubles as both the URL slug (`/industries/<id>`) and the
 * value used in `/contact?inquiry=industry&industry=<slug>` when we
 * funnel visitors from the industry pages to the contact form —
 * keeping them symmetrical means the contact-form pre-fill just
 * works.
 *
 * The mega-panel and detail pages share the same `mediaUrl`, so
 * swapping a cover video is a one-line change here.
 */

// Shared cover assets — the "explore by industry" overview clip is
// used by the sidebar drawer's category fallback preview AND by the
// /industries listing-page hero, so it lives on the media map next
// to the per-industry clips.
export const INDUSTRIES_MEDIA = {
  overview: 'https://istatec-assets.istatec.com/explore_by_industry_w.webm',
  banking: 'https://istatec-assets.istatec.com/Industries_financial_w.webm',
  consumer:
    'https://istatec-assets.istatec.com/Industries_Consumer-Services_w.webm',
  healthcare:
    'https://istatec-assets.istatec.com/Industries_helthcare_w.webm',
  insurance: 'https://istatec-assets.istatec.com/mobile-Insurance_w.webm',
  manufacturing:
    'https://istatec-assets.istatec.com/Industries_Manufacturing_w.webm',
  technology:
    'https://istatec-assets.istatec.com/Industries_technology_media_telicome_w.webm',
  publicSector: 'https://istatec-assets.istatec.com/public_sector_w.webm',
} as const;

export interface Industry {
  /**
   * URL slug (`/industries/<id>`) AND the value emitted by the
   * Contact form's `industry` query param. Keep symmetrical.
   */
  id: string;
  /** Display title — sidebar label, hero heading, form option text. */
  title: string;
  /**
   * Short 1-line pitch used in the sidebar drawer's preview column.
   * Reads well at ~15–16 px body copy. Kept intentionally to a
   * single sentence.
   */
  sidebarTagline: string;
  /**
   * Poster-style tagline shown inside the detail-page hero card.
   * One or two sentences, "we do X in this industry" voice.
   */
  heroTagline: string;
  /** Cover video URL (webm). */
  mediaUrl: string;
  /**
   * Heading for the "What sets us apart" section on the detail
   * page. Usually "Experience-led transformation for next-gen X".
   */
  whatSetsApartHeading: string;
  /**
   * Paragraph body for the "What sets us apart" section. Written in
   * "here's how we specifically approach this industry" voice —
   * concrete offerings, senior-engineer positioning, IstaLabs R&D
   * as the ongoing edge.
   */
  whatSetsApartDescription: string;
}

export const INDUSTRIES: readonly Industry[] = [
  {
    id: 'banking-and-financial-services',
    title: 'Banking and Financial Services',
    sidebarTagline:
      'Modernise core banking, ship AI-driven fraud and KYC/AML checks, and unify data across products so your teams can move at fintech pace inside a regulated environment.',
    heroTagline:
      'Reimagining banking experiences with digital agility, trust, and intelligence at the core.',
    mediaUrl: INDUSTRIES_MEDIA.banking,
    whatSetsApartHeading:
      'Experience-led transformation for next-gen Banking and Financial Services',
    whatSetsApartDescription:
      "Istatec's Financial Services practice leads with three pillars — deep domain expertise, product-engineering depth, and continuous R&D through IstaLabs. Combined with senior technical advisors and best-sourced expert engineers, we help banks, fintechs, and payment platforms ship faster while staying inside the regulator's lines. From core modernization and real-time risk analytics to AI-driven fraud detection and KYC/AML automation, we build systems that put customers first without loosening controls.",
  },
  {
    id: 'consumer-services',
    title: 'Consumer Services',
    sidebarTagline:
      'Faster storefronts, smarter recommendations, and unified customer data across every touchpoint — for retail, D2C, hospitality, and consumer platforms turning digital experience into repeat revenue.',
    heroTagline:
      'Building storefronts, experiences, and platforms that turn digital touchpoints into repeat revenue.',
    mediaUrl: INDUSTRIES_MEDIA.consumer,
    whatSetsApartHeading:
      'Experience-led transformation for next-gen Consumer Services',
    whatSetsApartDescription:
      "Retail, D2C, hospitality, and consumer platforms live and die on speed of iteration. Istatec pairs senior product engineers with GenAI personalisation stacks and unified customer-data platforms so your team can move from campaign-driven growth to compounding experiences. We build for teams that measure success in returning customers — not just first clicks — and every engagement is scoped to put a working slice in front of your team in weeks, not quarters.",
  },
  {
    id: 'healthcare-and-life-sciences',
    title: 'Healthcare and Life Sciences',
    sidebarTagline:
      'HIPAA-aware platforms, clinical-workflow automation, and AI-assisted decisioning that plug into existing EMRs. Built for hospitals, payers, medtech, and biotech teams who need real interoperability.',
    heroTagline:
      'Interoperable platforms and AI-assisted decisioning built for the regulations you actually work under.',
    mediaUrl: INDUSTRIES_MEDIA.healthcare,
    whatSetsApartHeading:
      'Experience-led transformation for next-gen Healthcare and Life Sciences',
    whatSetsApartDescription:
      "Healthcare software has to be right, not just fast. Istatec's healthcare practice combines HIPAA-aware architecture, EMR integrations, and clinical-workflow automation with the AI/GenAI advances that are actually mature enough to ship — augmented documentation, prior-authorization automation, imaging assistance. Every system we build for hospitals, payers, medtech, and biotech teams is designed to run at 3 AM, not just demo cleanly at 3 PM.",
  },
  {
    id: 'insurance',
    title: 'Insurance',
    sidebarTagline:
      'Underwriting automation, claims-triage AI, and policy-lifecycle platforms that shorten every cycle — for carriers, brokers, and insurtech teams moving from spreadsheets to intelligent decisioning.',
    heroTagline:
      'Underwriting, claims, and policy platforms that shorten every cycle.',
    mediaUrl: INDUSTRIES_MEDIA.insurance,
    whatSetsApartHeading:
      'Experience-led transformation for next-gen Insurance',
    whatSetsApartDescription:
      "Carriers, brokers, and insurtech teams come to Istatec when spreadsheets and legacy policy-admin systems can't keep up with modern customer expectations. We ship underwriting automation, claims-triage AI, and policy-lifecycle platforms that connect to what you already run — no big-bang re-platforms, just measurable cycle-time reduction from month one. Continuous R&D through IstaLabs means the newest decisioning patterns filter into your engagement without you funding the learning curve.",
  },
  {
    id: 'manufacturing',
    title: 'Manufacturing',
    sidebarTagline:
      'Connected-factory platforms, predictive maintenance, and supply-chain visibility built on top of the systems you already run — from plant-floor telemetry to enterprise ERP integrations.',
    heroTagline:
      'Connected factories, predictive operations, and supply chains that see around corners.',
    mediaUrl: INDUSTRIES_MEDIA.manufacturing,
    whatSetsApartHeading:
      'Experience-led transformation for next-gen Manufacturing',
    whatSetsApartDescription:
      "Manufacturing modernization is a data problem before it's a plant problem. Istatec brings the OT/IT integration, predictive-maintenance models, and supply-chain observability platforms that turn plant-floor telemetry into decisions your operations leaders can act on today. We meet you where your ERP and MES already are, and we build outward from there — no lab-conditions rewrites, just measurable improvements to uptime, throughput, and OEE.",
  },
  {
    id: 'technology-media-and-telecom',
    title: 'Technology, Media, and Telecom',
    sidebarTagline:
      'Product engineering for SaaS, media, and telecom operators — high-velocity platforms, AI-personalised experiences, and OSS/BSS modernisation that keeps you shipping ahead of the roadmap.',
    heroTagline:
      'Product engineering for the teams shipping the next generation of digital experiences.',
    mediaUrl: INDUSTRIES_MEDIA.technology,
    whatSetsApartHeading:
      'Experience-led transformation for next-gen Technology, Media, and Telecom',
    whatSetsApartDescription:
      "SaaS, media, and telecom operators need to ship faster than the roadmap without breaking scale. Istatec pairs senior product engineers with GenAI personalisation, OSS/BSS modernization, and platform-as-product delivery patterns to keep operator teams' ARPU and engineering velocity climbing at the same time. Every engagement is anchored to a working slice in weeks so your leadership sees value before the next planning cycle.",
  },
  {
    id: 'public-sector',
    title: 'Public Sector',
    sidebarTagline:
      'Secure, accessible citizen-service platforms and government-scale data infrastructure — built for public-sector teams working under real compliance, procurement, and accessibility requirements.',
    heroTagline:
      'Secure, accessible, scale-ready citizen services built for real compliance and real procurement.',
    mediaUrl: INDUSTRIES_MEDIA.publicSector,
    whatSetsApartHeading:
      'Experience-led transformation for next-gen Public Sector',
    whatSetsApartDescription:
      "Public-sector software has hard constraints — accessibility, procurement, security audits, records retention. Istatec's public-sector practice delivers under those constraints without cutting corners on the citizen experience. From data-sharing platforms to service-portal modernization to AI-assisted case management, we build systems that stand up to both the auditor and the taxpayer — with senior advisors on every call and best-sourced engineers who understand the difference between government scale and startup scale.",
  },
] as const;

/**
 * Section-header intro used by the /industries listing hero AND
 * (indirectly) the sidebar drawer's category-fallback preview.
 * Kept here so any wording change updates both surfaces at once.
 */
export const INDUSTRIES_OVERVIEW_DESCRIPTION =
  "From banking and healthcare to manufacturing and public sector, we bring senior technical advisors, best-sourced expert engineers, and an AI-first delivery approach to every industry we serve — meeting each sector's real regulations, workflows, and speed of play.";
